/**
 * Schema.org shared utilities.
 *
 * <p>Every JSON-LD generator (Course, Article, VideoObject, …) builds a
 * plain object then runs it through this module to emit a
 * {@code <script type="application/ld+json">} tag with the canonical
 * formatting Google's structured-data tools expect.
 *
 * <ul>
 *   <li>Generators ALWAYS emit a {@code <script>} tag — never return
 *       raw JSON for the caller to inject. Keeps output safe (we control
 *       escaping) and consistent.</li>
 *   <li>{@code undefined}/{@code null} fields are stripped recursively
 *       so the output doesn't show literal nulls — Schema validators
 *       flag those as type errors.</li>
 *   <li>Output is compact (not prettified) — bytes matter and the
 *       content is for machines, not humans.</li>
 * </ul>
 */
import type { ReactElement } from 'react';

export function stripUndefined<T>(value: T): T {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) {
    return value
      .map((v) => stripUndefined(v))
      .filter((v) => v !== undefined && v !== null) as unknown as T;
  }
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as object)) {
      const cleaned = stripUndefined(v);
      if (cleaned !== undefined && cleaned !== null) {
        out[k] = cleaned;
      }
    }
    return out as unknown as T;
  }
  return value;
}

export function JsonLdScript({ schema, id }: { schema: unknown; id?: string }): ReactElement {
  const json = JSON.stringify(stripUndefined(schema));
  return <script type="application/ld+json" id={id} dangerouslySetInnerHTML={{ __html: json }} />;
}
